/**
 * Página de Ajuda - Central de Ajuda do Sistema
 */

import { ArrowLeft, HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

export default function Ajuda() {
  return (
    <div className="min-h-screen gradient-warm">
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/auth?mode=login">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Voltar para login
            </Link>
          </Button>
        </div>
      </header>

      <main className="container max-w-4xl py-8 space-y-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <HelpCircle className="h-6 w-6 text-primary" />
              </div>
              <div className="flex-1">
                <CardTitle className="text-3xl">Central de Ajuda</CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Última atualização: 1º de novembro de 2025
                </p>
              </div>
            </div>
          </CardHeader>

          <Separator />

          <CardContent className="pt-6 space-y-8">
            {/* Seção 1 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  1
                </span>
                Bem-vindo ao Pudinzim Sorocaba
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  O Pudinzim Sorocaba é um sistema completo de gestão para confeitarias e padarias,
                  desenvolvido para facilitar o controle de estoque, vendas, produção e muito mais.
                </p>
                <p>
                  Com uma interface moderna e intuitiva, você pode gerenciar todos os aspectos do
                  seu negócio em um único lugar, com segurança e eficiência.
                </p>
              </div>
            </section>

            {/* Seção 2 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  2
                </span>
                Primeiros Passos
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>Para começar a usar o sistema, siga estes passos:</p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Faça login com suas credenciais fornecidas pelo administrador</li>
                  <li>Configure as informações básicas da sua empresa em Configurações</li>
                  <li>Cadastre os produtos, ingredientes e fornecedores</li>
                  <li>Configure as formas de pagamento e categorias</li>
                  <li>Comece a registrar entradas de estoque e vendas</li>
                </ol>
              </div>
            </section>

            {/* Seção 3 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  3
                </span>
                Funcionalidades Principais
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>O sistema oferece os seguintes módulos:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Dashboard:</strong> Visão geral do negócio com gráficos e indicadores</li>
                  <li><strong>Produtos:</strong> Cadastro e gestão de produtos e ingredientes</li>
                  <li><strong>Estoque:</strong> Controle de entradas, saídas e saldo atual</li>
                  <li><strong>Vendas:</strong> Registro de vendas e emissão de relatórios</li>
                  <li><strong>Fornadas:</strong> Planejamento e controle de produção</li>
                  <li><strong>Receitas:</strong> Gestão de receitas e fichas técnicas</li>
                  <li><strong>Clientes:</strong> Cadastro e histórico de clientes</li>
                  <li><strong>Relatórios:</strong> Análises e relatórios gerenciais</li>
                </ul>
              </div>
            </section>

            {/* Seção 4 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  4
                </span>
                Cadastros Básicos
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Antes de começar a operar o sistema, é importante realizar os cadastros básicos:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Produtos:</strong> Cadastre todos os produtos que você comercializa ou utiliza</li>
                  <li><strong>Tipos de Produto:</strong> Organize os produtos em categorias</li>
                  <li><strong>Unidades de Medida:</strong> Configure as unidades (kg, L, un, etc.)</li>
                  <li><strong>Fornecedores:</strong> Cadastre seus fornecedores de ingredientes</li>
                  <li><strong>Clientes:</strong> Registre os dados dos seus clientes</li>
                  <li><strong>Formas de Pagamento:</strong> Configure as formas de pagamento aceitas</li>
                </ul>
              </div>
            </section>

            {/* Seção 5 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  5
                </span>
                Gestão de Estoque
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  O módulo de estoque permite controlar todas as movimentações de produtos:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Entradas:</strong> Registre compras e recebimentos de produtos</li>
                  <li><strong>Saídas:</strong> Automaticamente registradas nas vendas e fornadas</li>
                  <li><strong>Saldo Atual:</strong> Visualize o estoque disponível em tempo real</li>
                  <li><strong>Alertas:</strong> Receba notificações de estoque baixo</li>
                  <li><strong>Histórico:</strong> Consulte todo o histórico de movimentações</li>
                </ul>
              </div>
            </section>

            {/* Seção 6 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  6
                </span>
                Vendas e Fornadas
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p><strong>Vendas:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Registre vendas de forma rápida e prática</li>
                  <li>Associe clientes às vendas</li>
                  <li>Escolha a forma de pagamento</li>
                  <li>Emita comprovantes e relatórios</li>
                </ul>
                <p className="mt-4"><strong>Fornadas:</strong></p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Planeje a produção com base nas receitas</li>
                  <li>Controle automaticamente o consumo de ingredientes</li>
                  <li>Acompanhe o status da produção</li>
                  <li>Calcule custos de produção automaticamente</li>
                </ul>
              </div>
            </section>

            {/* Seção 7 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  7
                </span>
                Relatórios e Análises
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Acesse relatórios detalhados para tomar decisões estratégicas:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Dashboard:</strong> Indicadores-chave em tempo real</li>
                  <li><strong>Vendas por Período:</strong> Análise de faturamento</li>
                  <li><strong>Produtos Mais Vendidos:</strong> Rankings e estatísticas</li>
                  <li><strong>Margem de Lucro:</strong> Análise de rentabilidade</li>
                  <li><strong>Movimentação de Estoque:</strong> Giro de produtos</li>
                </ul>
              </div>
            </section>

            {/* Seção 8 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  8
                </span>
                Dúvidas Frequentes (FAQ)
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <div className="space-y-3">
                  <div>
                    <p className="font-semibold text-foreground">Como recupero minha senha?</p>
                    <p>Clique em "Esqueceu a senha?" na tela de login e siga as instruções.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Posso usar o sistema offline?</p>
                    <p>Sim! Instale o aplicativo e utilize funcionalidades básicas mesmo sem internet.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Como adiciono novos usuários?</p>
                    <p>Apenas administradores podem convidar novos usuários em Configurações → Usuários.</p>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">Os dados são seguros?</p>
                    <p>Sim, utilizamos criptografia de ponta e backups automáticos diários.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Seção 9 */}
            <section className="space-y-4">
              <h2 className="text-xl font-semibold text-primary flex items-center gap-2">
                <span className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-sm">
                  9
                </span>
                Suporte e Contato
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  Caso precise de ajuda adicional, entre em contato com nossa equipe de suporte:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>E-mail:</strong> suporte@pudinzim.com.br</li>
                  <li><strong>WhatsApp:</strong> (15) 99999-9999</li>
                  <li><strong>Horário de Atendimento:</strong> Segunda a Sexta, 8h às 18h</li>
                </ul>
                <p className="mt-4">
                  Nossa equipe está sempre disponível para ajudá-lo a tirar o máximo proveito
                  do sistema Pudinzim Sorocaba.
                </p>
              </div>
            </section>
          </CardContent>
        </Card>
      </main>

      <footer className="border-t py-6 md:py-0">
        <div className="container flex flex-col items-center justify-center gap-4 md:h-16 md:flex-row">
          <p className="text-center text-sm text-muted-foreground">
            © 2025 Pudinzim Sorocaba. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
